import React, { useState, useEffect } from 'react';
import { X, Building2, Mail, Phone, MapPin, Tag, GraduationCap, CheckCircle, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { SimpleDropdown } from './ui/simple-dropdown';
import { SearchableDropdown } from './ui/searchable-dropdown';
import { IMaskInput } from 'react-imask';
import { educationAPI } from '../api/axiosClient';
import { CreateSchoolRequestV1, SchoolBrand, City, District, Neighborhood } from '../api/types';

interface SchoolCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedBrand: SchoolBrand | null;
  onBrandChange: (brand: SchoolBrand | null) => void;
  schoolBrands: SchoolBrand[];
  loadingSchoolBrands: boolean;
  cities: City[];
  districts: District[];
  neighborhoods: Neighborhood[];
  selectedProvince: { id: string; label: string; value: string } | null;
  selectedDistrict: { id: string; label: string; value: string } | null;
  selectedNeighborhood: { id: string; label: string; value: string } | null;
  onProvinceChange: (province: { id: string; label: string; value: string } | null) => void;
  onDistrictChange: (district: { id: string; label: string; value: string } | null) => void;
  onNeighborhoodChange: (neighborhood: { id: string; label: string; value: string } | null) => void;
  onFetchDistricts: (provinceId: string) => void;
  onFetchNeighborhoods: (districtId: string) => void;
}

const countryCodes = [
  { code: '90', flag: '🇹🇷', name: 'Türkiye' },
  { code: '1', flag: '🇺🇸', name: 'ABD' },
  { code: '44', flag: '🇬🇧', name: 'İngiltere' },
  { code: '49', flag: '🇩🇪', name: 'Almanya' },
  { code: '33', flag: '🇫🇷', name: 'Fransa' },
  { code: '39', flag: '🇮🇹', name: 'İtalya' },
  { code: '34', flag: '🇪🇸', name: 'İspanya' },
  { code: '31', flag: '🇳🇱', name: 'Hollanda' },
  { code: '32', flag: '🇧🇪', name: 'Belçika' },
  { code: '41', flag: '🇨🇭', name: 'İsviçre' },
  { code: '43', flag: '🇦🇹', name: 'Avusturya' },
  { code: '45', flag: '🇩🇰', name: 'Danimarka' },
  { code: '46', flag: '🇸🇪', name: 'İsveç' },
  { code: '47', flag: '🇳🇴', name: 'Norveç' },
  { code: '358', flag: '🇫🇮', name: 'Finlandiya' },
  { code: '48', flag: '🇵🇱', name: 'Polonya' },
  { code: '420', flag: '🇨🇿', name: 'Çekya' },
  { code: '421', flag: '🇸🇰', name: 'Slovakya' },
  { code: '36', flag: '🇭🇺', name: 'Macaristan' },
  { code: '40', flag: '🇷🇴', name: 'Romanya' },
  { code: '359', flag: '🇧🇬', name: 'Bulgaristan' },
  { code: '30', flag: '🇬🇷', name: 'Yunanistan' },
  { code: '385', flag: '🇭🇷', name: 'Hırvatistan' },
  { code: '386', flag: '🇸🇮', name: 'Slovenya' },
  { code: '381', flag: '🇷🇸', name: 'Sırbistan' },
  { code: '382', flag: '🇲🇪', name: 'Karadağ' },
  { code: '387', flag: '🇧🇦', name: 'Bosna Hersek' },
  { code: '389', flag: '🇲🇰', name: 'Makedonya' },
  { code: '355', flag: '🇦🇱', name: 'Arnavutluk' },
  { code: '7', flag: '🇷🇺', name: 'Rusya' },
  { code: '380', flag: '🇺🇦', name: 'Ukrayna' },
  { code: '375', flag: '🇧🇾', name: 'Belarus' },
  { code: '370', flag: '🇱🇹', name: 'Litvanya' },
  { code: '371', flag: '🇱🇻', name: 'Letonya' },
  { code: '372', flag: '🇪🇪', name: 'Estonya' },
  { code: '7', flag: '🇰🇿', name: 'Kazakistan' },
  { code: '998', flag: '🇺🇿', name: 'Özbekistan' },
  { code: '996', flag: '🇰🇬', name: 'Kırgızistan' },
  { code: '992', flag: '🇹🇯', name: 'Tacikistan' },
  { code: '993', flag: '🇹🇲', name: 'Türkmenistan' },
  { code: '994', flag: '🇦🇿', name: 'Azerbaycan' },
  { code: '374', flag: '🇦🇲', name: 'Ermenistan' },
  { code: '995', flag: '🇬🇪', name: 'Gürcistan' },
  { code: '90', flag: '🇹🇷', name: 'Kuzey Kıbrıs' }
];

const getPhoneMask = (countryCode: string): string => {
  switch (countryCode) {
    case '90':
      return '000 000 00 00';
    case '1':
      return '(000) 000-0000';
    case '44':
      return '0000 000000';
    case '49':
      return '000 00000000';
    case '33':
      return '0 00 00 00 00';
    case '39':
      return '000 000 0000';
    case '34':
      return '000 00 00 00';
    case '31':
      return '00 0000 0000';
    case '32':
      return '000 00 00 00';
    case '41':
      return '00 000 00 00';
    case '43':
      return '000 0000000';
    case '45':
      return '00 00 00 00';
    case '46':
      return '000-000 00 00';
    case '47':
      return '000 00 000';
    case '358':
      return '000 0000000';
    case '48':
      return '000 000 000';
    case '420':
      return '000 000 000';
    case '421':
      return '000 000 000';
    case '36':
      return '000 000 0000';
    case '40':
      return '000 000 0000';
    case '359':
      return '000 000 000';
    case '30':
      return '000 000 0000';
    case '385':
      return '000 000 0000';
    case '386':
      return '000 000 000';
    case '381':
      return '000 000 0000';
    case '382':
      return '000 000 000';
    case '387':
      return '000 000 000';
    case '389':
      return '000 000 000';
    case '355':
      return '000 000 0000';
    case '7':
      return '000 000 0000';
    case '380':
      return '000 000 0000';
    case '375':
      return '000 000 0000';
    case '370':
      return '000 00000';
    case '371':
      return '0000 0000';
    case '372':
      return '0000 0000';
    case '998':
      return '000 000 0000';
    case '996':
      return '000 000 000';
    case '992':
      return '000 000 000';
    case '993':
      return '000 000 000';
    case '994':
      return '000 000 0000';
    case '374':
      return '000 000 000';
    case '995':
      return '000 000 000';
    default:
      return '000 000 0000';
  }
};

const getPhonePlaceholder = (countryCode: string): string => {
  switch (countryCode) {
    case '90':
      return '555 123 45 67';
    case '1':
      return '(555) 123-4567';
    case '44':
      return '7700 900123';
    case '49':
      return '30 12345678';
    case '33':
      return '1 23 45 67 89';
    case '39':
      return '312 345 6789';
    case '34':
      return '612 34 56 78';
    case '31':
      return '06 12345678';
    case '32':
      return '470 12 34 56';
    case '41':
      return '79 123 45 67';
    case '43':
      return '664 1234567';
    case '45':
      return '12 34 56 78';
    case '46':
      return '070-123 45 67';
    case '47':
      return '123 45 678';
    case '358':
      return '40 1234567';
    case '48':
      return '123 456 789';
    case '420':
      return '123 456 789';
    case '421':
      return '123 456 789';
    case '36':
      return '20 123 4567';
    case '40':
      return '712 345 678';
    case '359':
      return '87 123 4567';
    case '30':
      return '691 234 5678';
    case '385':
      return '91 234 5678';
    case '386':
      return '31 234 567';
    case '381':
      return '60 123 4567';
    case '382':
      return '67 123 456';
    case '387':
      return '61 123 456';
    case '389':
      return '70 123 456';
    case '355':
      return '69 123 4567';
    case '7':
      return '912 345 6789';
    case '380':
      return '50 123 4567';
    case '375':
      return '29 123 4567';
    case '370':
      return '612 34567';
    case '371':
      return '2123 4567';
    case '372':
      return '5123 4567';
    case '998':
      return '90 123 4567';
    case '996':
      return '555 123 456';
    case '992':
      return '90 123 4567';
    case '993':
      return '60 123 456';
    case '994':
      return '50 123 4567';
    case '374':
      return '77 123 456';
    case '995':
      return '555 123 456';
    default:
      return '555 123 4567';
  }
};

const getSchoolTypeLabel = (schoolType: string): string => {
  switch (schoolType) {
    case 'Public':
      return 'Devlet Okulu';
    case 'Private':
      return 'Özel Okul';
    case 'Kindergarten':
      return 'Anaokulu';
    case 'Primary':
      return 'İlkokul';
    case 'Secondary':
      return 'Ortaokul';
    case 'HighSchool':
      return 'Lise';
    case 'College':
      return 'Kolej';
    case 'Other':
      return 'Diğer';
    default:
      return 'Seçiniz';
  }
};

const getSchoolTypeNumber = (schoolType: string): number => {
  switch (schoolType) {
    case 'Public': return 1;
    case 'Private': return 2;
    case 'Kindergarten': return 3;
    case 'Primary': return 4;
    case 'Secondary': return 5;
    case 'HighSchool': return 6;
    case 'College': return 7;
    case 'Other': return 8;
    default: return 4; // Default to Primary
  }
};

const SchoolCreateForm: React.FC<SchoolCreateFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedBrand,
  onBrandChange,
  schoolBrands,
  loadingSchoolBrands,
  cities,
  districts,
  neighborhoods,
  selectedProvince,
  selectedDistrict,
  selectedNeighborhood,
  onProvinceChange,
  onDistrictChange,
  onNeighborhoodChange,
  onFetchDistricts,
  onFetchNeighborhoods
}) => {
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    email: '',
    phoneCode: '90',
    phoneNumber: '',
    schoolType: '',
    address: '',
    province: '',
    district: '',
    neighborhood: '',
    isActive: true,
    brandName: ''
  });

  const [selectedCountryCode, setSelectedCountryCode] = useState<{ id: string; label: string; value: string }>(
    { id: '90', label: '🇹🇷 +90', value: '90' }
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setSchoolForm({
        name: '',
        email: '',
        phoneCode: '90',
        phoneNumber: '',
        schoolType: '',
        address: '',
        province: '',
        district: '',
        neighborhood: '',
        isActive: true,
        brandName: ''
      });
      setSelectedCountryCode({ id: '90', label: '🇹🇷 +90', value: '90' });
      setFormErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setSchoolForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!schoolForm.name.trim()) {
      errors.name = 'Okul adı gereklidir';
    }

    if (!schoolForm.email.trim()) {
      errors.email = 'E-posta gereklidir';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(schoolForm.email)) {
      errors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!schoolForm.phoneNumber.trim()) {
      errors.phoneNumber = 'Telefon numarası gereklidir';
    }

    if (!schoolForm.schoolType) {
      errors.schoolType = 'Okul türü gereklidir';
    }

    if (!selectedProvince) {
      errors.province = 'İl seçimi gereklidir';
    }

    if (!selectedDistrict) {
      errors.district = 'İlçe seçimi gereklidir';
    }

    if (!selectedNeighborhood) {
      errors.neighborhood = 'Mahalle seçimi gereklidir';
    }

    if (!schoolForm.address.trim()) {
      errors.address = 'Adres gereklidir';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      const createPayload: CreateSchoolRequestV1 = {
        name: schoolForm.name.trim(),
        email: schoolForm.email.trim(),
        phoneCode: schoolForm.phoneCode,
        phoneNumber: schoolForm.phoneNumber.replace(/\s+/g, ''),
        schoolType: getSchoolTypeNumber(schoolForm.schoolType),
        address: schoolForm.address.trim(),
        cityId: schoolForm.province,
        districtId: schoolForm.district,
        neighborhoodId: schoolForm.neighborhood,
        isActive: schoolForm.isActive,
        schoolBrandId: selectedBrand ? selectedBrand.id : null
      };

      const response = await educationAPI.createSchool(createPayload);
      
      if (response.success) {
        onSuccess();
        onClose();
      } else {
        alert('Okul eklenirken bir hata oluştu');
      }
      
    } catch (error) {
      console.error('Error creating school:', error);
      alert('Okul eklenirken bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ margin: 0, padding: '1rem' }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Yeni Okul Ekle
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Sol Sütun */}
            <div className="space-y-6">
              {/* Okul Adı */}
              <div>
                <Label htmlFor="schoolName" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <Building2 className="w-4 h-4 mr-2" />
                  Okul Adı *
                </Label>
                <Input
                  id="schoolName"
                  name="name"
                  type="text"
                  value={schoolForm.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Okul adını girin"
                  className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                    formErrors.name ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* E-posta */}
              <div>
                <Label htmlFor="schoolEmail" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <Mail className="w-4 h-4 mr-2" />
                  E-posta *
                </Label>
                <Input
                  id="schoolEmail"
                  name="email"
                  type="email"
                  value={schoolForm.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="ornek@okul.com"
                  className={`w-full rounded-md border bg-white px-3 py-2 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400 ${
                    formErrors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>

              {/* Okul Türü */}
              <div>
                <Label htmlFor="schoolType" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Okul Türü *
                </Label>
                <SimpleDropdown
                  options={[
                    { id: 'Public', label: 'Devlet Okulu', value: 'Public' },
                    { id: 'Private', label: 'Özel Okul', value: 'Private' },
                    { id: 'Kindergarten', label: 'Anaokulu', value: 'Kindergarten' },
                    { id: 'Primary', label: 'İlkokul', value: 'Primary' },
                    { id: 'Secondary', label: 'Ortaokul', value: 'Secondary' },
                    { id: 'HighSchool', label: 'Lise', value: 'HighSchool' },
                    { id: 'College', label: 'Kolej', value: 'College' },
                    { id: 'Other', label: 'Diğer', value: 'Other' }
                  ]}
                  value={{ id: schoolForm.schoolType, label: getSchoolTypeLabel(schoolForm.schoolType), value: schoolForm.schoolType }}
                  onChange={(schoolType) => {
                    if (schoolType) {
                      handleInputChange('schoolType', String(schoolType.value));
                    }
                  }}
                  placeholder="Okul türü seçiniz"
                  disabled={isSubmitting}
                />
                {formErrors.schoolType && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.schoolType}</p>
                )}
              </div>

              {/* Telefon */}
              <div>
                <Label htmlFor="schoolPhone" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <Phone className="w-4 h-4 mr-2" />
                  Telefon *
                </Label>
                <div className="flex space-x-2">
                  <SimpleDropdown
                    options={countryCodes.map((code) => ({
                      id: code.code,
                      label: `${code.flag} +${code.code}`,
                      value: code.code
                    }))}
                    value={selectedCountryCode}
                    onChange={(countryCode) => {
                      if (countryCode) {
                        setSelectedCountryCode(countryCode as { id: string; label: string; value: string });
                        handleInputChange('phoneCode', String(countryCode.value));
                        handleInputChange('phoneNumber', ''); // Reset phone number when country changes
                      }
                    }}
                    placeholder="Ülke seçiniz"
                    disabled={isSubmitting}
                    className="w-24"
                  />
                  <IMaskInput
                    id="schoolPhone"
                    name="phoneNumber"
                    type="tel"
                    mask={getPhoneMask(schoolForm.phoneCode || '90')}
                    placeholder={getPhonePlaceholder(schoolForm.phoneCode || '90')}
                    value={schoolForm.phoneNumber || ''}
                    onAccept={(value: string) => {
                      handleInputChange('phoneNumber', value);
                    }}
                    className={`flex-1 text-left text-sm font-mono px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                      formErrors.phoneNumber ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                    }`}
                    inputMode="numeric"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.phoneNumber}</p>
                )}
              </div>

              {/* Aktif Durum */}
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Aktif Durum
                </Label>
                <RadioGroup
                  value={schoolForm.isActive ? "active" : "inactive"}
                  onValueChange={(value: string) => setSchoolForm(prev => ({ ...prev, isActive: value === "active" }))}
                  className="flex items-center space-x-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="active" id="schoolActive" className="text-[#9B51E0] border-gray-300 focus:ring-[#9B51E0] focus:ring-offset-2" />
                    <Label htmlFor="schoolActive" className="text-sm text-gray-700">
                      Aktif
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="inactive" id="schoolInactive" className="text-[#9B51E0] border-gray-300 focus:ring-[#9B51E0] focus:ring-offset-2" />
                    <Label htmlFor="schoolInactive" className="text-sm text-gray-700">
                      Pasif
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Sağ Sütun */}
            <div className="space-y-6">
              {/* Marka */}
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <Tag className="w-4 h-4 mr-2" />
                  Marka
                </Label>
                <SearchableDropdown
                  options={schoolBrands.map(brand => ({
                    id: brand.id,
                    label: brand.name,
                    value: brand.id
                  }))}
                  value={selectedBrand ? { id: selectedBrand.id, label: selectedBrand.name, value: selectedBrand.id } : null}
                  onChange={(brand) => {
                    if (brand) {
                      const schoolBrand = schoolBrands.find(b => b.id === brand.value);
                      if (schoolBrand) {
                        onBrandChange(schoolBrand);
                        handleInputChange('brandName', schoolBrand.name);
                      }
                    } else {
                      onBrandChange(null);
                      handleInputChange('brandName', '');
                    }
                  }}
                  placeholder="Marka ara veya seç..."
                  clearable={true}
                  disabled={isSubmitting}
                  isLoading={loadingSchoolBrands}
                  noResultsText="Marka bulunamadı"
                />
                {formErrors.brand && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.brand}</p>
                )}
              </div>

              {/* İl */}
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  İl *
                </Label>
                <SimpleDropdown
                  options={cities.map(city => ({
                    id: city.id,
                    label: city.name,
                    value: city.id
                  }))}
                  value={selectedProvince}
                  onChange={(province) => {
                    onProvinceChange(province as { id: string; label: string; value: string } | null);
                    onDistrictChange(null);
                    onNeighborhoodChange(null);
                    handleInputChange('province', String(province?.value || ''));
                    handleInputChange('district', '');
                    handleInputChange('neighborhood', '');
                    
                    // Load districts when province changes
                    if (province?.value) {
                      onFetchDistricts(String(province.value));
                    }
                  }}
                  placeholder="İl seçiniz"
                  disabled={isSubmitting}
                />
                {formErrors.province && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.province}</p>
                )}
              </div>

              {/* İlçe */}
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  İlçe *
                </Label>
                <SimpleDropdown
                  options={districts.map(district => ({
                    id: district.id,
                    label: district.name,
                    value: district.id
                  }))}
                  value={selectedDistrict}
                  onChange={(district) => {
                    onDistrictChange(district as { id: string; label: string; value: string } | null);
                    onNeighborhoodChange(null);
                    handleInputChange('district', String(district?.value || ''));
                    handleInputChange('neighborhood', '');
                    
                    // Load neighborhoods when district changes
                    if (district?.value) {
                      onFetchNeighborhoods(String(district.value));
                    }
                  }}
                  placeholder="İlçe seçiniz"
                  disabled={!selectedProvince || isSubmitting}
                  noResultsText="İlçe bulunamadı"
                />
                {formErrors.district && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.district}</p>
                )}
              </div>

              {/* Mahalle */}
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Mahalle *
                </Label>
                <SimpleDropdown
                  options={neighborhoods.map(neighborhood => ({
                    id: neighborhood.id,
                    label: neighborhood.name,
                    value: neighborhood.id
                  }))}
                  value={selectedNeighborhood}
                  onChange={(neighborhood) => {
                    onNeighborhoodChange(neighborhood as { id: string; label: string; value: string } | null);
                    handleInputChange('neighborhood', String(neighborhood?.value || ''));
                  }}
                  placeholder="Mahalle seçiniz"
                  disabled={!selectedDistrict || isSubmitting}
                  noResultsText="Mahalle bulunamadı"
                />
                {formErrors.neighborhood && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.neighborhood}</p>
                )}
              </div>

              {/* Adres */}
              <div>
                <Label htmlFor="schoolAddress" className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <MapPin className="w-4 h-4 mr-2" />
                  Detay Adres *
                </Label>
                <textarea
                  id="schoolAddress"
                  name="address"
                  value={schoolForm.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Sokak, bina no, kat bilgisi vb."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 resize-none transition hover:border-gray-400 ${
                    formErrors.address ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  disabled={isSubmitting}
                />
                {formErrors.address && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-8 border-t border-gray-200 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#9B51E0] hover:bg-[#8B51E0]"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Ekleniyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchoolCreateForm;
